import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { http } from "@/lib/api";
import { useLoad, useStaff } from "@/lib/staff";
import { BTN_GHOST, Badge, ErrorBox, INPUT, Skeleton, Table } from "@/components/staff/ui";
import { ConfirmAction } from "@/components/staff/ConfirmAction";

const KEYS = ["in_per_m", "out_per_m", "per_request"];
const SOURCE = { default: ["neutral", "Default"], custom: ["sky", "Edited"], none: ["amber", "No rate"] };

function changedRows(rows, original) {
  return rows.filter((r) => {
    const o = original.find((x) => x.model === r.model);
    return !o || KEYS.some((k) => Number(o[k]) !== Number(r[k]));
  });
}

function columns(edit, change) {
  const cell = (k, label) => ({
    key: k, label, right: true,
    render: (r) => (edit
      ? <input type="number" min={0} step="any" value={r[k]} onChange={(e) => change(r.model, k, e.target.value)} className={`${INPUT} w-28 text-right`} aria-label={`${label} for ${r.model}`} data-testid={`rate-${k}-${r.model}`} />
      : `$${Number(r[k]).toString()}`),
  });
  return [
    { key: "model", label: "Model", render: (r) => <span className="font-mono text-xs">{r.model}</span> },
    cell("in_per_m", "$ per 1M input tokens"), cell("out_per_m", "$ per 1M output tokens"), cell("per_request", "$ per request"),
    { key: "source", label: "Rate", right: true, render: (r) => <Badge tone={(SOURCE[r.source] || SOURCE.custom)[0]}>{(SOURCE[r.source] || SOURCE.custom)[1]}</Badge> },
  ];
}

export function RatesTab() {
  const { can } = useStaff();
  const edit = can("costs.config");
  const { data, error, loading, reload } = useLoad("/staff/rates");
  const [rows, setRows] = useState([]);
  const [model, setModel] = useState("");
  useEffect(() => { if (data) setRows(data.rates.map((r) => ({ ...r }))); }, [data]);
  const change = (m, k, v) => setRows((rs) => rs.map((r) => (r.model === m ? { ...r, [k]: v } : r)));
  const add = () => {
    const m = model.trim();
    if (!m || rows.some((r) => r.model === m)) return;
    setRows((rs) => [...rs, { model: m, in_per_m: 0, out_per_m: 0, per_request: 0, source: "custom" }]);
    setModel("");
  };
  const changed = data ? changedRows(rows, data.rates) : [];
  const bad = changed.some((r) => KEYS.some((k) => r[k] === "" || Number(r[k]) < 0 || Number(r[k]) > (k === "per_request" ? 100 : 1000)));
  const save = async (reason) => {
    await http.put("/staff/rates", { rates: changed.map((r) => ({ model: r.model, ...Object.fromEntries(KEYS.map((k) => [k, Number(r[k])])) })), reason });
    toast.success("AI cost rates saved");
    reload();
  };
  return (
    <div className="space-y-4" data-testid="rates-tab">
      <p className="text-sm text-neutral-400">These rates turn estimated tokens into the "Estimated AI cost" figures. Changing them recalculates past estimates too. They never change what students pay.</p>
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!data && loading ? <Skeleton rows={4} /> : (
        <Table testid="rates-table" columns={columns(edit, change)} rows={rows} rowKey={(r) => r.model} empty="No AI models have been used yet." maxH="max-h-none" />
      )}
      {edit && data && (
        <div className="flex flex-wrap items-center gap-2">
          <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model name, e.g. claude-sonnet-4-5" className={`${INPUT} w-72`} aria-label="New model name" data-testid="rates-new-model" />
          <button onClick={add} disabled={!model.trim()} className={BTN_GHOST} data-testid="rates-add-model"><Plus className="h-4 w-4" />Add model</button>
          <span className="flex-1" />
          <ConfirmAction label={changed.length ? `Save ${changed.length} change${changed.length === 1 ? "" : "s"}` : "Save changes"} icon={Save} primary disabled={!changed.length || bad} testid="rates-save"
            title="Save AI cost rates?" confirmLabel="Save rates" description="Estimated costs across the dashboard will use the new rates." run={save} />
        </div>
      )}
      {!edit && <p className="text-xs text-neutral-500">Only the owner and Super Admins can edit these rates.</p>}
    </div>
  );
}
